import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/apiClient.js';
import {
  Drawer,
  FormField,
  Textarea,
  TextInput,
  Select,
  DrawerForm,
  DrawerFormActions,
} from '../../components/index.js';
import { StatusChip } from '../../components/primitives.jsx';
import { fieldLabel, humanizeEnum } from '../../lib/fieldLabels.js';

// Derive the authoritative-source options from a case's conflicting systems so the
// steward picks a REAL source (e.g. ServiceNow vs Jira) rather than an abstract
// "System A". Reads labelled conflict fields ({systemALabel, systemAValue,
// systemBLabel, systemBValue}) and/or an explicit sourceSystems[] array. Returns
// [] for legacy cases with no labelled systems (caller falls back to generic).
export function deriveSourceOptions(caseData) {
  if (!caseData) return [];
  const byValue = new Map();
  const add = (label, prefill) => {
    const key = String(label || '').trim();
    if (!key || byValue.has(key)) return;
    byValue.set(key, {
      value: key,
      optionLabel: prefill != null && prefill !== '' ? `${key} — ${prefill}` : key,
      prefill: prefill == null ? '' : String(prefill),
    });
  };
  const fields = Array.isArray(caseData.conflictFields) ? caseData.conflictFields : [];
  for (const cf of fields) {
    if (!cf || typeof cf !== 'object') continue;
    add(cf.systemALabel, cf.systemAValue);
    add(cf.systemBLabel, cf.systemBValue);
  }
  const systems = Array.isArray(caseData.sourceSystems) ? caseData.sourceSystems : [];
  for (const s of systems) {
    if (typeof s === 'string') add(s);
    else if (s && typeof s === 'object') add(s.system || s.label, s.value);
  }
  return Array.from(byValue.values());
}

const ACTION_OPTIONS = [
  { value: 'resolve', label: 'Resolve — pick the authoritative value' },
  { value: 'defer', label: 'Defer — review next sprint' },
  { value: 'reassign', label: 'Reassign — hand off to another steward' },
];

// How long the resolved-confirmation stays visible before the drawer auto-closes.
const RESOLVED_AUTOCLOSE_MS = 1600;

// Direct-finding mode (C1): a case-less data-quality finding opens this same
// drawer built from the finding itself — no case fetch, no case-only controls,
// resolve-only — so resolving it still demands a written rationale instead of
// the old silent one-click patch.
export function ReconciliationDrawer({ open, onClose, caseId, finding = null, onResolved, submitting }) {
  const [caseData, setCaseData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(null);
  // Drawer lifecycle: 'idle' shows the form, 'submitting' while the resolve is in
  // flight, 'resolved' shows the success confirmation before an auto-close. This
  // guarantees a successful resolve never reverts to a dangling "Loading case…".
  const [phase, setPhase] = useState('idle');
  const [resolvedSummary, setResolvedSummary] = useState('');
  const [action, setAction] = useState('resolve');
  const [authoritativeSource, setAuthoritativeSource] = useState('system_a');
  const [authoritativeValueText, setAuthoritativeValueText] = useState('');
  const [rationale, setRationale] = useState('');
  const [newOwnerUserId, setNewOwnerUserId] = useState('');
  const [formError, setFormError] = useState(null);
  const closeTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (!open || (!caseId && !finding)) {
      setCaseData(null);
      setLoaded(false);
      setPhase('idle');
      setResolvedSummary('');
      return;
    }
    setErr(null);
    setLoaded(false);
    setPhase('idle');
    setResolvedSummary('');
    if (!caseId && finding) {
      // Direct-finding mode: the drawer's subject IS the finding — no case
      // record exists to fetch.
      setCaseData({
        id: finding.id,
        title: finding.title || finding.id,
        status: finding.status || 'open',
        severity: finding.severity || null,
        canonicalEntityType: finding.canonicalEntityType || null,
        description: finding.description || finding.recommendation || null,
        directFinding: true,
      });
      setLoaded(true);
      setAction('resolve');
      setAuthoritativeSource('system_a');
      setAuthoritativeValueText('');
      setRationale('');
      setNewOwnerUserId('');
      setFormError(null);
      return;
    }
    api
      .get(`/reconciliation-cases/${caseId}`)
      // A resolved case (or an edge with no open conflict) can return an empty
      // body — treat a payload with no id/title as "nothing to load" so the render
      // shows an explicit empty state rather than an infinite spinner.
      .then((r) => {
        const data = r?.data ?? r;
        setCaseData(data && (data.id || data.title) ? data : null);
      })
      .catch((e) => setErr(e))
      .finally(() => setLoaded(true));
    setAction('resolve');
    setAuthoritativeSource('system_a');
    setAuthoritativeValueText('');
    setRationale('');
    setNewOwnerUserId('');
    setFormError(null);
  }, [open, caseId, finding]);

  const conflictFields = useMemo(() => {
    if (!caseData) return [];
    return Array.isArray(caseData.conflictFields) ? caseData.conflictFields : [];
  }, [caseData]);
  // Legacy seed cases store conflictFields as plain field-name strings; split
  // them from the structured {systemALabel/value} entries so each renders in a
  // shape that actually carries information.
  const structuredConflictFields = useMemo(
    () => conflictFields.filter((cf) => cf && typeof cf === 'object'),
    [conflictFields],
  );
  const legacyConflictFieldNames = useMemo(
    () => conflictFields.filter((cf) => typeof cf === 'string' && cf.trim()),
    [conflictFields],
  );

  // Real conflicting source systems for this case (e.g. ServiceNow vs Jira).
  const sourceOptions = useMemo(() => deriveSourceOptions(caseData), [caseData]);

  // When a case loads, seed the authoritative source + value from its real
  // conflicting systems; legacy cases fall back to the generic System A default.
  useEffect(() => {
    if (!caseData) return;
    if (sourceOptions.length) {
      setAuthoritativeSource(sourceOptions[0].value);
      setAuthoritativeValueText(sourceOptions[0].prefill ?? '');
    } else {
      setAuthoritativeSource('system_a');
      setAuthoritativeValueText('');
    }
  }, [caseData, sourceOptions]);

  // Switching the authoritative source reprefills the value with that system's
  // reported value (still editable for a manual override).
  function handleSourceChange(next) {
    setAuthoritativeSource(next);
    const opt = sourceOptions.find((o) => o.value === next);
    if (opt) setAuthoritativeValueText(opt.prefill ?? '');
    else if (next === 'other') setAuthoritativeValueText('');
  }

  // Friendly label for the chosen authoritative source, for the success summary.
  function authoritativeSourceLabel() {
    const opt = sourceOptions.find((o) => o.value === authoritativeSource);
    if (opt) return opt.value;
    if (authoritativeSource === 'system_a') return 'System A';
    if (authoritativeSource === 'system_b') return 'System B';
    if (authoritativeSource === 'other') return 'the canonical override';
    return authoritativeSource;
  }

  function successSummaryFor(actionValue) {
    if (caseData?.directFinding) return 'The finding is resolved with the rationale recorded.';
    if (actionValue === 'resolve') {
      const value = authoritativeValueText ? ` (${authoritativeValueText})` : '';
      return `${authoritativeSourceLabel()} is now authoritative${value}.`;
    }
    if (actionValue === 'defer') return 'Deferred for review next sprint.';
    return 'Reassigned to a new steward.';
  }

  async function handleSubmit() {
    if (!rationale.trim()) {
      setFormError('Rationale is required.');
      return;
    }
    if (action === 'reassign' && !newOwnerUserId.trim()) {
      setFormError('Provide the new owner user ID.');
      return;
    }
    const body = {
      action,
      rationale: rationale.trim(),
    };
    if (caseData?.directFinding) {
      body.directFinding = true;
    } else {
      if (action === 'resolve') {
        body.authoritativeSource = authoritativeSource;
        body.authoritativeValue = authoritativeValueText || null;
      }
      if (action === 'reassign') {
        body.newOwnerUserId = newOwnerUserId.trim();
      }
    }
    setFormError(null);
    setPhase('submitting');
    try {
      // The parent resolves the API call and surfaces its own failure toast; it
      // resolves on success and rejects on failure so the drawer can confirm or
      // return the steward to the form to retry.
      await onResolved?.(body, caseData);
      setResolvedSummary(successSummaryFor(action));
      setPhase('resolved');
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        onClose?.();
      }, RESOLVED_AUTOCLOSE_MS);
    } catch {
      setPhase('idle');
    }
  }

  const busy = submitting || phase === 'submitting';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        phase === 'resolved' ? 'Conflict resolved' : caseData?.title || 'Reconcile data conflict'
      }
      width={560}
    >
      {phase === 'resolved' ? (
        <div className="recon-resolved" data-testid="recon-resolved">
          <div className="banner green">
            <strong>Conflict resolved.</strong> {resolvedSummary}
          </div>
          <p className="muted small">This drawer will close automatically.</p>
          <div className="drawer-form-actions">
            <div />
            <div className="drawer-form-actions-right">
              <button type="button" className="btn primary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : err ? (
        <div className="banner red">
          We couldn&apos;t load this reconciliation case. Please try again.
        </div>
      ) : !caseData ? (
        loaded ? (
          <div className="recon-empty" data-testid="recon-empty">
            <div className="muted">
              This reconciliation case is already resolved or no longer has an open conflict.
            </div>
            <div className="drawer-form-actions">
              <div />
              <div className="drawer-form-actions-right">
                <button type="button" className="btn ghost sm" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="muted">Loading case…</div>
        )
      ) : (
        <DrawerForm onSubmit={handleSubmit} submitting={busy}>
          <div className="recon-meta">
            <StatusChip status={caseData.severity || 'unknown'}>
              {caseData.severity ? humanizeEnum(caseData.severity) : 'Severity not set'}
            </StatusChip>
            <StatusChip status={caseData.status} />
            <span className="chip outline sm" title={caseData.canonicalEntityType}>
              {humanizeEnum(caseData.canonicalEntityType, 'Entity')}
            </span>
          </div>

          {structuredConflictFields.length ? (
            <div className="recon-conflict-table">
              <div className="eyebrow">Conflict fields</div>
              <table>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>
                      {structuredConflictFields.find((cf) => cf?.systemALabel)?.systemALabel ||
                        'System A'}
                    </th>
                    <th>
                      {structuredConflictFields.find((cf) => cf?.systemBLabel)?.systemBLabel ||
                        'System B'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {structuredConflictFields.map((cf, i) => (
                    <tr key={i}>
                      <td title={cf.field || cf.name || undefined}>
                        {cf.field || cf.name ? fieldLabel(cf.field || cf.name) : `Field ${i + 1}`}
                      </td>
                      <td>{String(cf.systemAValue ?? cf.valueA ?? '—')}</td>
                      <td>{String(cf.systemBValue ?? cf.valueB ?? '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {legacyConflictFieldNames.length ? (
            // Legacy cases carry conflictFields as plain field names with no
            // per-system values — name them readably instead of dash cells.
            <div className="recon-conflict-table">
              <div className="eyebrow">Fields in conflict</div>
              <p className="muted small">
                {legacyConflictFieldNames.map((name) => fieldLabel(name)).join(' · ')}
              </p>
            </div>
          ) : null}

          {caseData.directFinding && caseData.description ? (
            <p className="muted small">{caseData.description}</p>
          ) : null}

          {caseData.directFinding ? null : (
            <FormField label="Action" htmlFor="recon-action" required>
              <Select
                id="recon-action"
                value={action}
                onChange={setAction}
                options={ACTION_OPTIONS}
              />
            </FormField>
          )}

          {!caseData.directFinding && action === 'resolve' ? (
            sourceOptions.length ? (
              <>
                <FormField label="Authoritative source" htmlFor="recon-source" required>
                  <Select
                    id="recon-source"
                    value={authoritativeSource}
                    onChange={handleSourceChange}
                    options={[
                      ...sourceOptions.map((o) => ({ value: o.value, label: o.optionLabel })),
                      { value: 'other', label: 'Other / canonical override' },
                    ]}
                  />
                </FormField>
                <FormField
                  label="Authoritative value"
                  htmlFor="recon-value"
                  hint="Prefilled from the selected source system — edit to override."
                >
                  <TextInput
                    id="recon-value"
                    value={authoritativeValueText}
                    onChange={setAuthoritativeValueText}
                    placeholder="e.g. blocked"
                  />
                </FormField>
              </>
            ) : (
              <>
                <FormField label="Authoritative source" htmlFor="recon-source">
                  <Select
                    id="recon-source"
                    value={authoritativeSource}
                    onChange={setAuthoritativeSource}
                    options={[
                      { value: 'system_a', label: 'System A (first source)' },
                      { value: 'system_b', label: 'System B (second source)' },
                      { value: 'other', label: 'Other / canonical override' },
                    ]}
                  />
                </FormField>
                {authoritativeSource === 'other' ? (
                  <FormField label="Override value" htmlFor="recon-value">
                    <Textarea
                      id="recon-value"
                      value={authoritativeValueText}
                      onChange={setAuthoritativeValueText}
                      placeholder="Free-text authoritative value (e.g. 4.2)"
                      rows={2}
                    />
                  </FormField>
                ) : null}
              </>
            )
          ) : null}

          {action === 'reassign' ? (
            <FormField label="New owner user ID" htmlFor="recon-newowner" required>
              <Textarea
                id="recon-newowner"
                value={newOwnerUserId}
                onChange={setNewOwnerUserId}
                placeholder="e.g. USR-DATA-KIM"
                rows={1}
              />
            </FormField>
          ) : null}

          <FormField label="Rationale" htmlFor="recon-rationale" required error={formError}>
            <Textarea
              id="recon-rationale"
              value={rationale}
              onChange={setRationale}
              placeholder="Why is this the right call? Reference the conversation, ticket, or evidence."
              rows={4}
            />
          </FormField>

          <DrawerFormActions
            onCancel={onClose}
            submitLabel={
              caseData.directFinding
                ? 'Resolve finding'
                : action === 'resolve'
                  ? 'Resolve conflict'
                  : action === 'defer'
                    ? 'Defer'
                    : 'Reassign'
            }
            submitting={busy}
          />
        </DrawerForm>
      )}
    </Drawer>
  );
}
